const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
const formatScore = (value) => Number(value || 0).toFixed(1);
const PERFORMANCE_DETAIL_THRESHOLD = 3;
const SUPPRESSED_DETAIL = `Suppressed: fewer than ${PERFORMANCE_DETAIL_THRESHOLD} completed diagnostics`;

export const followUpSignal = (program) => {
  if ((program.completedCount || 0) >= 5 || (program.startedCount || 0) >= 8) {
    return "High";
  }

  if ((program.startedCount || 0) >= 3) {
    return "Medium";
  }

  return "Watch";
};

const toDateString = (date) => date.toISOString().slice(0, 10);

const formatFilter = (value) => value || "All";

const formatPgyBreakdown = (pgyBreakdown = {}) => {
  const entries = Object.entries(pgyBreakdown);

  if (!entries.length) {
    return "None reported";
  }

  return entries.map(([pgy, count]) => `${pgy}: ${count}`).join(", ");
};

const formatWeakAreas = (areas = []) => {
  if (!areas.length) {
    return "None reported";
  }

  return areas
    .map((area) => `${area.name} (${formatPercent(area.percentageCorrect)})`)
    .join(", ");
};

const canShowPerformanceDetail = (program) => (
  (program.completedCount || 0) >= PERFORMANCE_DETAIL_THRESHOLD
);

export const getProgramMetricsReportFilename = (generatedAt = new Date()) => (
  `absite-program-metrics-${toDateString(generatedAt)}.md`
);

export const buildProgramMetricsMarkdownReport = ({
  generatedAt = new Date(),
  filters = {},
  totals = {},
  programs = []
}) => {
  const generatedDate = toDateString(generatedAt);
  const completionRate = totals.completionRate ?? (
    totals.startedCount
      ? (totals.completedCount * 100) / totals.startedCount
      : 0
  );

  const lines = [
    "# ABSITE Pilot Weekly Report",
    "",
    `Generated: ${generatedDate}`,
    `Program code: ${formatFilter(filters.programCode)}`,
    `Date range: ${formatFilter(filters.from)} to ${formatFilter(filters.to)}`,
    "Data source: QLearn ABSITE Diagnostic Program Metrics",
    "",
    "## Summary",
    "",
    `- Programs: ${totals.programCount || 0}`,
    `- Residents started: ${totals.startedCount || 0}`,
    `- Residents completed: ${totals.completedCount || 0}`,
    `- Completion rate: ${formatPercent(completionRate)}`,
    `- Unique residents: ${totals.uniqueResidentCount || 0}`,
    "",
    "## Program Summary",
    "",
    "| Program | Started | Completed | Completion | Residents | Average score | Follow-up |",
    "| --- | ---: | ---: | ---: | ---: | ---: | --- |",
    ...programs.map((program) => {
      const averageScore = canShowPerformanceDetail(program)
        ? formatScore(program.averageScore)
        : "Suppressed";

      return `| ${program.programCode} | ${program.startedCount || 0} | ${program.completedCount || 0} | ${formatPercent(program.completionRate)} | ${program.uniqueResidentCount || 0} | ${averageScore} | ${followUpSignal(program)} |`;
    }),
    "",
    "## PGY Breakdown",
    "",
    ...programs.map((program) => (
      `- ${program.programCode}: ${canShowPerformanceDetail(program) ? formatPgyBreakdown(program.pgyBreakdown) : SUPPRESSED_DETAIL}`
    )),
    "",
    "## Top Weak Areas",
    "",
    ...programs.map((program) => (
      `- ${program.programCode}: ${canShowPerformanceDetail(program) ? formatWeakAreas(program.topWeakAreas) : SUPPRESSED_DETAIL}`
    )),
    "",
    "## Privacy Check",
    "",
    "- Aggregate only: yes",
    `- Performance detail suppressed below ${PERFORMANCE_DETAIL_THRESHOLD} completed diagnostics: yes`,
    "- No resident identifiers: yes",
    "- No quiz IDs: yes",
    "- No official ABSITE scores: yes",
    ""
  ];

  return lines.join("\n");
};

export const downloadProgramMetricsReport = ({ report, filename }) => {
  const blob = new Blob([report], { type: "text/markdown;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
